import type { DurableChatTurnRegistryState } from "./durable-chat-turn-registry-types"
import type {
  ActivityMessage,
  DurableChatTurnEnrichedSession,
  DurableChatTurnEvent,
  DurableChatTurnIdentity,
  DurableChatTurnPendingControl,
  DurableChatTurnSnapshot,
  DurableChatTurnStatus,
  ResponseContext,
} from "@/api/models"

export const DEFAULT_MAX_BUFFERED_EVENTS = 128
export const DEFAULT_MAX_RETAINED_EVENTS = 256
export const DEFAULT_BASE_RECOVERY_DELAY_MS = 500
export const DEFAULT_MAX_RECOVERY_DELAY_MS = 10_000
export const DEFAULT_STATUS_POLL_INTERVAL_MS = 2_000

const TERMINAL_STATUSES = new Set<DurableChatTurnStatus>([
  "completed",
  "failed",
  "cancelled",
  "interrupted",
])

type RegistryStateLike = DurableChatTurnIdentity & {
  events: DurableChatTurnEvent[]
  snapshot: DurableChatTurnSnapshot
}

export function isTerminalStatus(status: DurableChatTurnStatus): boolean {
  return TERMINAL_STATUSES.has(status)
}

/** Capped exponential backoff: `base * 2^attempt`, clamped to `max`.
 *
 *  Shared by `DurableChatTurnSubscription`'s WS reconnect and
 *  `DurableChatTurnRegistry`'s recovery retry (adversarial review, reuse
 *  finding) — both used to compute this identically but independently,
 *  which meant a future tuning change (e.g. adding jitter) had to be
 *  found and applied in two places by hand. One implementation now,
 *  varying only in which `base`/`max` each caller passes. */
export function computeBackoffDelayMs(
  attempt: number,
  { base, max }: { base: number; max: number }
): number {
  return Math.min(base * 2 ** attempt, max)
}

export function targetKey({ sessionId, turnId, epoch }: DurableChatTurnIdentity): string {
  return JSON.stringify([sessionId, turnId, epoch])
}

export function matchesTarget(
  expected: DurableChatTurnIdentity,
  candidate: DurableChatTurnIdentity
): boolean {
  return (
    expected.sessionId === candidate.sessionId &&
    expected.turnId === candidate.turnId &&
    expected.epoch === candidate.epoch
  )
}

/**
 * Reconcile an authoritative covering snapshot's `content` with what the
 * client already has, for `DurableChatTurnRegistry.replaceFromSnapshot`.
 *
 * `contentOmitted` (set by `_durable_snapshot_body` in chat_turn_runner.py
 * instead of truncating `content` when the answer alone busts the live
 * snapshot's byte budget) means `turnSnapshot` is still authoritative for
 * status/context/activities/controls but NOT for content: its `content`, if
 * present at all, is a SUFFIX anchored at `snapshotSeq`, while
 * `previousContent` is a PREFIX ending at the client's OLD cursor — neither
 * contains the other, so blending them (or picking "whichever is longer")
 * would risk silently dropping the newest text. Keeping `previousContent`
 * instead is the only option that never shows something false: subsequent
 * live `content` events keep appending to it, and the eventual complete
 * answer always arrives via the persisted final message / history reload,
 * exactly as it does today for every other terminal status.
 */
export function resolveContentOmittedSnapshot(
  turnSnapshot: DurableChatTurnSnapshot,
  previousContent: string
): DurableChatTurnSnapshot {
  return turnSnapshot.contentOmitted ? { ...turnSnapshot, content: previousContent } : turnSnapshot
}

/** The state `DurableChatTurnRegistry.replaceFromSnapshot` swaps in for an
 *  authoritative covering snapshot (a replay gap, HTTP recovery, or a
 *  terminal frame) — see `resolveContentOmittedSnapshot` above for why
 *  `content` specifically is handled differently from every other field. */
export function replacedRegistryState(
  state: DurableChatTurnRegistryState,
  turnSnapshot: DurableChatTurnSnapshot
): DurableChatTurnRegistryState {
  return {
    ...state,
    cursor: turnSnapshot.snapshotSeq,
    events: [],
    snapshot: resolveContentOmittedSnapshot(turnSnapshot, state.snapshot.content),
    status: statusWithPendingControls(turnSnapshot.status, turnSnapshot),
    terminal: isTerminalStatus(turnSnapshot.status),
  }
}

/**
 * The header status is advisory: the runner records the pending control (and
 * the covering snapshot) BEFORE stamping `waiting-approval`, and that stamp is
 * best-effort (`_transition_status` defers on a storage blip). So a status
 * read over HTTP or announced on attach can still say `running` while the
 * event log / snapshot already carries a pending control. The control is the
 * fact; the header only echoes it — never let the echo hide the card (#2962).
 */
export function statusWithPendingControls(
  status: DurableChatTurnStatus,
  snapshot: Pick<DurableChatTurnSnapshot, "controls">
): DurableChatTurnStatus {
  if (isTerminalStatus(status)) return status
  return snapshot.controls.length > 0 ? "waiting-approval" : status
}

/**
 * The turn status implied by one live `turn_event`, given the status the
 * registry currently holds.
 *
 * The subscription socket announces the header status exactly once
 * (`turn_accepted`, at attach time) and then streams only events; a turn
 * that parks on a tool approval afterwards is stamped `waiting-approval` in
 * the backend header, but no frame ever carries that change to a live
 * subscriber. `mapDurableChatTurnStateToSessionPatch` renders the pending
 * controls ONLY under `waiting-approval` (its `running` branch clears
 * `toolApprovals` on purpose), so without this the approval card never
 * showed for the tab that sent the message: the tool timed out and was
 * denied while the UI sat on "Using <tool> tool…" (#2962, the ordinary-chat
 * half). The durable event log is authoritative and the two control events
 * are exactly the runner's status transitions (`chat_turn_runner.py`:
 * `CHUNK_AWAIT_DECISION` → waiting-approval, `_resolve_control` → running),
 * so derive the status from them here. Terminal statuses never move.
 */
export function statusAfterEvent(
  status: DurableChatTurnStatus,
  turnEvent: DurableChatTurnEvent,
  snapshot: DurableChatTurnSnapshot
): DurableChatTurnStatus {
  if (isTerminalStatus(status)) return status
  switch (turnEvent.eventType) {
    case "control_requested":
      // A request event without a usable control folds to no pending
      // controls; there is nothing to answer, so do not park the UI.
      return snapshot.controls.length > 0 ? "waiting-approval" : status
    case "control_resolved":
      return status === "waiting-approval" ? "running" : status
    default:
      return status
  }
}

/**
 * The registry state after one contiguous `turn_event` is appended: the event
 * folded onto the live snapshot, the cursor advanced, and the status derived
 * from the event (`control_requested`/`control_resolved` are the runner's own
 * waiting-approval/running transitions, and the socket never re-announces the
 * header status after `turn_accepted` — see `statusAfterEvent`).
 */
export function foldEventOntoState(
  state: DurableChatTurnRegistryState,
  turnEvent: DurableChatTurnEvent,
  events: DurableChatTurnEvent[]
): DurableChatTurnRegistryState {
  const snapshot = applyEventToSnapshot(state.snapshot, turnEvent)
  const status = statusAfterEvent(state.status, turnEvent, snapshot)
  return {
    ...state,
    cursor: turnEvent.seq,
    events,
    snapshot: status === snapshot.status ? snapshot : { ...snapshot, status },
    status,
  }
}

export function cloneRegistryState<T extends RegistryStateLike>(state: T): T {
  return {
    ...state,
    events: [...state.events],
    snapshot: {
      ...state.snapshot,
      activities: [...state.snapshot.activities],
      controls: [...state.snapshot.controls],
    },
  }
}

export function snapshotsEqual(
  first: DurableChatTurnSnapshot,
  second: DurableChatTurnSnapshot
): boolean {
  // Snapshots are JSON DTOs. Terminal ownership is separately guarded by the
  // nonterminal-to-terminal state transition.
  return JSON.stringify(first) === JSON.stringify(second)
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : undefined
}

/**
 * Fold one `turn_event` onto the registry's live `snapshot` — mirrors the
 * backend's `_SnapshotAccumulator.apply_event` (chat_turns_ws.py) so a
 * hydrated/subscribed viewer's `snapshot.content` (and context/activities/
 * controls) advances as events arrive rather than staying frozen at the
 * attach-time snapshot until the next `turn_snapshot`/terminal frame
 * (issue #2513 finding 4). Event-type strings are the wire contract shared
 * with `EVENT_CONTENT`/`EVENT_CONTEXT`/etc. in `services/chat_turn_runner.py`
 * — there is no generated/shared enum to import here.
 *
 * Unknown event types and malformed `data` are no-ops: this is a best-effort
 * live projection only. The next authoritative `turn_snapshot` (replay gap)
 * or HTTP status/recover read always wins and fully replaces `snapshot`
 * regardless of what this accumulated in the meantime.
 */
export function applyEventToSnapshot(
  snapshot: DurableChatTurnSnapshot,
  turnEvent: DurableChatTurnEvent
): DurableChatTurnSnapshot {
  const data = asRecord(turnEvent.data)
  if (!data) return snapshot

  switch (turnEvent.eventType) {
    case "content": {
      const delta = data.content
      if (typeof delta !== "string") return snapshot
      return { ...snapshot, content: snapshot.content + delta }
    }
    case "context": {
      const context = asRecord(data.context)
      if (!context) return snapshot
      return {
        ...snapshot,
        context: { ...snapshot.context, ...(context as Partial<ResponseContext>) },
      }
    }
    case "activity": {
      if (data.activity === undefined) return snapshot
      return {
        ...snapshot,
        activities: [...snapshot.activities, data.activity as ActivityMessage],
      }
    }
    case "response_complete":
      return { ...snapshot, responseComplete: true }
    case "control_requested": {
      const control = data.control
      return {
        ...snapshot,
        controls: control ? [control as DurableChatTurnPendingControl] : [],
      }
    }
    case "control_resolved":
      return snapshot.controls.length === 0 ? snapshot : { ...snapshot, controls: [] }
    case "steer_status": {
      // Per-note outcome for a durable steer note (#2814). Keyed by the
      // client's own `steer_id` so the composer chip settles exactly as it
      // does on the WebSocket transport. A note the runner deferred reports
      // nothing until it truly lands, so `pending` never appears here.
      const steerId = data.steerId
      const status = data.status
      if (typeof steerId !== "string" || !steerId) return snapshot
      if (status !== "delivered" && status !== "undelivered" && status !== "rejected") {
        return snapshot
      }
      return {
        ...snapshot,
        steerStatuses: { ...(snapshot.steerStatuses ?? {}), [steerId]: status },
      }
    }
    case "session_enriched": {
      // Whole-value replacement, matching the backend's own
      // `_SnapshotAccumulator` (`self.session = dict(session)`, never a
      // field-by-field merge) — the runner always republishes the full
      // enriched projection, so folding a stale partial onto a newer one can
      // never happen (issue #2513). Delivering it onto `snapshot.session` is
      // necessary but not sufficient: callers still decide how (and whether)
      // to mirror it onto the visible session list — see
      // `attachDurableChatTurnToSession` in use-durable-chat-turn-hydration.ts.
      const session = asRecord(data.session)
      if (!session) return snapshot
      return { ...snapshot, session: session as unknown as DurableChatTurnEnrichedSession }
    }
    default:
      return snapshot
  }
}
